// src/services/equipment/equipmentRegisterMapper.js

import { getCanonicalRequirementsByCategory } from '../requirements/canonicalRequirements.js';
import { getFFEGuidance } from '../../config/guidance/ffe.guidance.js';
import { getLSAGuidance } from '../../config/guidance/lsa.guidance.js';
import { getNAVGuidance } from '../../config/guidance/nav.guidance.js';
import { getGMDSSGuidance } from '../../config/guidance/gmdss.guidance.js';
import { getElectricalGuidance } from '../../config/guidance/electrical.guidance.js';
import { getMachineryGuidance } from '../../config/guidance/machinery.guidance.js';
import { getGeneralGuidance } from '../../config/guidance/general.guidance.js';

// ——— Guidance helper ———

/**
 * Check all guidance sources for equipment item
 * @param {string} itemId - Equipment requirement ID or name
 * @returns {boolean} True if guidance exists for this item
 */
function hasGuidance(itemId) {
  return !!(
    getFFEGuidance(itemId) ||
    getLSAGuidance(itemId) ||
    getNAVGuidance(itemId) ||
    getGMDSSGuidance(itemId) ||
    getElectricalGuidance(itemId) ||
    getMachineryGuidance(itemId) ||
    getGeneralGuidance(itemId)
  );
}

// ——— Canonical helpers ———

const toKey = (s) => String(s || '').toLowerCase().trim();
const inferLocationFromName = (name) => {
  const n = toKey(name);
  if (n.includes('bridge') || n.includes('control')) return 'control_position';
  if (n.includes('galley')) return 'galley';
  if (n.includes('engine')) return 'engine';
  if (n.includes('wheelhouse')) return 'wheelhouse';
  if (n.includes('sleeping') || n.includes('quarters')) return 'sleeping_quarters';
  if (n.includes('accommodation') || n.includes('cabin')) return 'accommodation';
  if (n.includes('battery')) return 'battery';
  if (n.includes('electrical')) return 'electrical';
  return 'general';
};

// Normalize names so label variants collapse (Galley Fixed Fire System ⇄ Fixed Fire System – Galley)
const canonicalName = (type, label) => {
  const s = toKey(label);
  if (type === 'fixedSystem' || s.includes('fixed fire')) {
    if (s.includes('galley')) return 'fixed fire system - galley';
    if (s.includes('engine')) return 'fixed fire system - engine';
    if (s.includes('accommodation')) return 'fixed fire system - accommodation';
    return 'fixed fire system';
  }
  if (type === 'blanket' || s.includes('blanket')) return 'fire blanket';
  if (type === 'extinguisher' || s.includes('portable extinguisher')) {
    if (s.includes('bridge') || s.includes('control')) return 'portable extinguisher - control position';
    if (s.includes('engine')) return 'portable extinguisher - engine';
    if (s.includes('galley') && s.includes('additional')) return 'portable extinguisher - galley (8a/68b) — additional';
    if (s.includes('galley') && s.includes('8a/68b')) return 'portable extinguisher - galley (8a/68b)';
    if (s.includes('galley')) return 'portable extinguisher - galley (5a/34b)';
    if (s.includes('sleeping') || s.includes('quarters')) return 'portable extinguisher - sleeping quarters (5a/34b)';
    if (s.includes('wheelhouse')) return 'portable extinguisher - wheelhouse (5a/34b)';
    if (s.includes('accommodation')) return 'portable extinguisher - accommodation (5a/34b)';
    return 'portable extinguisher';
  }
  if (s.includes('fire pump')) return 'fire pump';
  if (s.includes('fire hose')) return 'fire hose';
  if (s.includes('fire control') || s.includes('safety plan')) return 'fire control & safety plan';
  if (s.includes('escape route') || s.includes('signage')) return 'escape route marking & signage';
  if (s.includes('detector') && s.includes('lithium')) return 'fire detector - lithium battery';
  if (s.includes('detector') && s.includes('machinery')) return 'fire detector - machinery space';
  if (s.includes('detector') && s.includes('open') && s.includes('flame')) return 'fire detector - open flame area';
  if (s.includes('detector') && s.includes('accommodation')) return 'fire detector - accommodation';
  // IMPORTANT: Check 'hydrocarbon' BEFORE 'carbon' - "hydrocarbon" contains the substring "carbon"
  // which would incorrectly match the CO detector pattern if checked second
  if (s.includes('gas detector') && s.includes('hydrocarbon')) return 'gas detector - hydrocarbon';
  if (s.includes('gas detector') && (s.includes('carbon') || s.includes('co'))) return 'gas detector - co (accommodation)';
  return s || 'equipment';
};

// The single canonical key for dedupe (NO requirement id in the key)
const canonicalKey = ({ type, label, location }) => {
  const loc = toKey(location || inferLocationFromName(label));
  const name = canonicalName(type, label);
  return `${toKey(type)}|${name}|${loc}`;
};

// Deterministic ID (stable forever) - includes sourceRequirementId to prevent duplicates
const rowId = ({ vesselId, type, location, variant = 'base', sourceRequirementId }) =>
  sourceRequirementId ? 
    `reg:${vesselId}:${toKey(sourceRequirementId)}:${toKey(variant)}` :
    `reg:${vesselId}:${toKey(type)}:${toKey(location || 'general')}:${toKey(variant)}`;

// Friendly labels (kept short; mapper is canonical, not UX heavy)
const labelFor = ({ type, location, variant, lifejacketType }) => {
  const loc = (location || 'General').replace(/_/g, ' ');
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const L = cap(loc);
  if (type === 'fixedSystem') return `Fixed Fire System - ${L}`;
  if (type === 'blanket') return `Fire Blanket`;
  if (type === 'extinguisher') {
    if (L === 'Galley' && variant === '8a68b') return `Portable Extinguisher - Galley (8A/68B)`;
    if (L === 'Galley' && variant === '8a68b_extra') return `Portable Extinguisher - Galley (8A/68B) — Additional`;
    if (L === 'Galley') return `Portable Extinguisher - Galley (5A/34B)`;
    if (L === 'Control position' || L === 'Control_position') return `Portable Extinguisher - Control Position`;
    if (L === 'Sleeping quarters' || L === 'Sleeping_quarters') return `Portable Extinguisher - Sleeping Quarters (5A/34B)`;
    if (L === 'Wheelhouse') return `Portable Extinguisher - Wheelhouse (5A/34B)`;
    return `Portable Extinguisher - ${L}`;
  }
  if (type === 'pump') return 'Fire Pump';
  if (type === 'hose') return 'Fire Hose';
  if (type === 'document') return 'Fire Control & Safety Plan';
  if (type === 'signage') return 'Escape Route Marking & Signage';
  if (type === 'detector') {
    // Handle gas detector variants (CO, hydrocarbon)
    if (variant === 'co' && L === 'Galley') return 'CO Detector - Galley';
    if (variant === 'co') return 'CO Detector - Accommodation';
    if (variant === 'hydrocarbon' && L === 'Galley') return 'Hydrocarbon Gas Detector - Galley';
    if (variant === 'hydrocarbon') return 'Hydrocarbon Gas Detector';
    
    // Regular fire detectors (only if no variant specified)
    if (!variant) {
      if (L === 'Battery') return 'Fire Detector - Lithium Battery';
      if (L === 'Engine') return 'Fire Detector - Machinery Space';
      if (L === 'Galley') return 'Fire Detector - Galley';
      if (L === 'Accommodation') return 'Fire Detector - Accommodation';
      return 'Fire Detector';
    }
    
    // Default detector label if variant is unknown
    return `Detector - ${L}`;
  }
  if (type === 'gas') {
    // Legacy support for old gas type (shouldn't be used anymore)
    if (variant === 'co' && L === 'Galley') return 'CO Detector - Galley';
    if (variant === 'co') return 'CO Detector - Accommodation';
    if (variant === 'hydrocarbon' && L === 'Galley') return 'Hydrocarbon Gas Detector - Galley';
    if (variant === 'hydrocarbon') return 'Hydrocarbon Gas Detector';
    return 'Gas Detector';
  }

  // ===== LSA EQUIPMENT LABELS =====
  
  if (type === 'lifejacket') {
    if (variant === 'child') {
      return lifejacketType === 'inflatable' ? 'Lifejackets (Child) - Inflatable' : 'Lifejackets (Child)';
    }
    if (variant === 'spare') return 'Spare Lifejackets (Inflatable)';
    if (variant === 'adult') {
      return lifejacketType === 'inflatable' ? 'Lifejackets (Adult) - Inflatable' : 'Lifejackets (Adult)';
    }
    return lifejacketType === 'inflatable' ? 'Lifejackets - Inflatable' : 'Lifejackets';
  }
  
  if (type === 'lifebuoy') {
    return 'Lifebuoys';
  }
  
  if (type === 'liferaft') {
    if (variant === 'cat0') return 'Liferafts (Cat 0) - 200% POB';
    if (variant === 'cat1_pob16plus') return 'Liferafts (Cat 1 ≥16 POB) - 200% POB';
    if (variant === 'cat1_pobunder16') return 'Liferafts (Cat 1 <16 POB) - 100% POB';
    if (variant === 'cat2to6') return 'Liferafts (Cat 2-6) - 100% POB';
    return 'Liferafts';
  }
  
  if (type === 'rescue_boat') {
    if (variant === 'fast') return 'Fast Rescue Boat';
    return 'Rescue Boat';
  }
  
  if (type === 'immersion_suit') {
    return 'Immersion Suits';
  }
  
  if (type === 'tpa') {
    if (variant === 'cats0to5') return 'TPA (Categories 0-5)';
    if (variant === 'cat6_cold') return 'TPA (Cat 6 Cold Water)';
    if (variant === 'cat6_openraft') return 'TPA (Cat 6 Open Raft)';
    return 'Thermal Protective Aids (TPA)';
  }
  
  if (type === 'pyrotechnic') {
    if (variant === 'pilot_white') return 'White Parachute Flares (Pilot Boat)';
    if (variant === 'pilot_red') return 'Red Rocket Parachute Flares (Pilot Boat)';
    if (variant === 'flares') return 'Distress Pyrotechnics';
    return 'Pyrotechnics';
  }
  
  if (type === 'epirb') {
    if (variant === 'cat0') return 'EPIRB Cat 0 (2 required)';
    if (variant === 'cat1to2') return 'EPIRB Cat 1-2';
    return 'EPIRB';
  }
  
  if (type === 'sart') {
    return 'SART or AIS-SART';
  }
  
  if (type === 'general_alarm') {
    return 'General Alarm System';
  }

  // ===== MEDICAL EQUIPMENT LABELS =====

  if (type === 'medical_stores') {
    if (variant === 'cat_a') return 'Medical Stores Category A';
    if (variant === 'cat_b') return 'Medical Stores Category B';
    if (variant === 'cat_c') return 'Medical Stores Category C';
    return 'Medical Stores';
  }

  if (type === 'medical_kit') {
    if (variant === 'dg') return 'Dangerous Goods Medical Kit';
    if (variant === 'rouv') return 'Portable Medical Kit (ROUV)';
    return 'Medical Kit';
  }

  if (type === 'antidote_kit') {
    return 'Specific Antidotes (DG)';
  }

  if (type === 'first_aid_kit') {
    if (variant === 'police_pwc') return 'First Aid Kit (Police PWC)';
    return 'First Aid Kit';
  }

  if (type === 'stretcher') {
    if (variant === 'pilot') return 'Compact Stretcher (Pilot Boat)';
    return 'Stretcher';
  }

  return `Equipment - ${L}`;
};

//— Mapping table for direct requirements (single row per requirement)
const DIRECT = {
  'ffe_fixed_system_engine':          { type: 'fixedSystem', location: 'engine' },
  // 'ffe_fixed_system_galley':          { type: 'fixedSystem', location: 'galley' }, // REMOVED - this requirement no longer exists (handled by choice in ffe_protection_open_flame)
  'ffe_fixed_system_accommodation':   { type: 'fixedSystem', location: 'accommodation' },

  'ffe_fire_pump_required':           { type: 'pump', location: 'general' },
  'ffe_fire_hose_fitted':             { type: 'hose', location: 'general' },
  'ffe_fire_buckets_fitted':          { type: 'bucket', location: 'general' },

  // ===== Fire Detectors - Location-Specific =====
  'ffe_detector_machinery':                { type: 'detector', location: 'engine' },
  'ffe_detector_fire_galley':              { type: 'detector', location: 'galley' },
  'ffe_detector_fire_sleeping':            { type: 'detector', location: 'sleeping_quarters' },
  'ffe_detector_fire_wheelhouse':          { type: 'detector', location: 'wheelhouse' },
  'ffe_detector_lithium_system':           { type: 'detector', location: 'battery' },

  // Legacy detector IDs (kept for backward compatibility)
  'ffe_detector_accommodation':            { type: 'detector', location: 'accommodation' },
  'ffe_detector_openflame_spaces':         { type: 'detector', location: 'galley' },
  'ffe_detector_fire_galley_openflame':    { type: 'detector', location: 'galley', variant: 'openflame' }, // DEPRECATED: Now consolidated into ffe_detector_fire_galley

  // ===== CO Detectors - Location-Specific =====
  'ffe_detector_co_galley':                { type: 'detector', location: 'galley', variant: 'co' },
  'ffe_detector_co_accommodation':         { type: 'detector', location: 'accommodation', variant: 'co' },
  'ffe_detector_co_exhaust':               { type: 'detector', location: 'engine', variant: 'co' },

  // ===== Hydrocarbon Gas Detectors - Location-Specific =====
  'ffe_detector_gas_galley':               { type: 'detector', location: 'galley', variant: 'hydrocarbon' },
  'ffe_detector_gas_storage':              { type: 'gas', location: 'gas_storage', variant: 'hydrocarbon' },

  // Legacy gas detector IDs (kept for backward compatibility)
  'ffe_detector_gas_hydrocarbon':          { type: 'gas', location: 'galley', variant: 'hydrocarbon' },
  'ffe_detector_gas_co':                   { type: 'gas', location: 'accommodation', variant: 'co' },

  'ffe_extinguisher_control_position':        { type: 'extinguisher', location: 'control_position' },
  'ffe_extinguisher_inboard_lt120kw':         { type: 'extinguisher', location: 'engine' },
  // 'ffe_extinguisher_enginebox_above':         { type: 'extinguisher', location: 'engine' }, // REMOVED - handled in choices to avoid duplicate
  'ffe_extinguisher_outboard_le220kw':        { type: 'extinguisher', location: 'engine' },
  'ffe_extinguisher_outboard_gt220kw':        { type: 'extinguisher', location: 'engine' },
  'ffe_extinguisher_galley':                  { type: 'extinguisher', location: 'galley', variant: '8a68b' },
  'ffe_extinguisher_cooking_flame':           { type: 'extinguisher', location: 'galley' }, // 5A/34B base
  'ffe_extinguisher_cooking_noflame':         { type: 'extinguisher', location: 'galley' }, // 5A/34B electric/induction
  'ffe_extinguisher_openboat_le8m':           { type: 'extinguisher', location: 'general' }, // 2×34B for open boats <8m

  // Accommodation extinguishers - per space (WBC3 16.2.1.2 requires one PER EACH accommodation space)
  'ffe_extinguisher_accommodation_sleeping':   { type: 'extinguisher', location: 'sleeping_quarters' },
  'ffe_extinguisher_accommodation_wheelhouse': { type: 'extinguisher', location: 'wheelhouse' },

  // Legacy accommodation extinguisher IDs (kept for backward compatibility with old vessel configs)
  'ffe_extinguisher_accommodation_basic':      { type: 'extinguisher', location: 'accommodation' },
  'ffe_extinguisher_accommodation_sleeping_only': { type: 'extinguisher', location: 'accommodation' },

  'ffe_fire_blanket_risk_area':               { type: 'blanket', location: 'galley' },

  // Fixed systems
  'ffe_fixed_system_accommodation':           { type: 'fixed_system', location: 'accommodation' },

  // Fire pumps
  'ffe_fire_pump_dangerous_goods':            { type: 'pump', location: 'general' },

  // Insulation
  'ffe_insulation_a60_dangerous_goods':       { type: 'insulation', location: 'general' },

  // Documents and signage
  'ffe_plan_fire_control':                    { type: 'document', location: 'general' },
  'ffe_escape_route_marking':                 { type: 'signage', location: 'general' },

  // Prohibition notices
  'ffe_extinguisher_pilot_boat_no_petrol':    { type: 'prohibition', location: 'general' },

  // ===== LSA EQUIPMENT MAPPINGS =====

  // Lifejackets
  'lsa.lifejackets.adult':                    { type: 'lifejacket', location: null, variant: 'adult' },
  'lsa.lifejackets.child':                    { type: 'lifejacket', location: null, variant: 'child' },
  'lsa.lifejackets.spare':                    { type: 'lifejacket', location: null, variant: 'spare' },

  // Lifebuoys
  'lsa.lifebuoys.standard':                   { type: 'lifebuoy', location: null },

  // Liferafts
  'lsa.liferafts.cat0':                       { type: 'liferaft', location: null, variant: 'cat0' },
  'lsa.liferafts.cat1.pob16plus':             { type: 'liferaft', location: null, variant: 'cat1_pob16plus' },
  'lsa.liferafts.cat1.pobunder16':            { type: 'liferaft', location: null, variant: 'cat1_pobunder16' },
  'lsa.liferafts.cat2to6':                    { type: 'liferaft', location: null, variant: 'cat2to6' },

  // Rescue craft  
  'lsa.lifeboat':                             { type: 'lifeboat', location: null }, // Step 5 wizard persistence fix
  'lsa.rescue.boat':                          { type: 'rescue_boat', location: null },
  'lsa.fast.rescue.boat':                     { type: 'rescue_boat', location: null, variant: 'fast' },

  // Immersion suits
  'lsa.immersion.suits':                      { type: 'immersion_suit', location: null },

  // Thermal protective aids (TPA)
  'lsa.tpa.cats0to5':                         { type: 'tpa', location: null, variant: 'cats0to5' },
  'lsa.tpa.cat6.cold':                        { type: 'tpa', location: null, variant: 'cat6_cold' },
  'lsa.tpa.cat6.openraft':                    { type: 'tpa', location: null, variant: 'cat6_openraft' },
  'lsa.tpa.wizard':                           { type: 'tpa', location: null, variant: 'wizard_selected' },

  // Line throwing apparatus
  'lsa.line.throwing':                        { type: 'line_throwing', location: null },
  'lsa.line.throwing.pilot':                  { type: 'line_throwing', location: null, variant: 'pilot' },

  // MOB recovery equipment
  'lsa.recovery.equipment':                   { type: 'recovery_equipment', location: null },
  'lsa.pilot.lifelines':                      { type: 'lifeline', location: null, variant: 'pilot' },
  'lsa.recovery.pilot.equipment':             { type: 'recovery_equipment', location: null, variant: 'pilot' },
  'lsa.mob.prevention.pilot':                 { type: 'mob_prevention', location: null, variant: 'pilot' },
  'lsa.throw.line.police':                    { type: 'throw_line', location: null, variant: 'police' },
  'lsa.recovery.police.unconscious':          { type: 'recovery_equipment', location: null, variant: 'police_unconscious' },
  'lsa.mob.police.general':                   { type: 'mob_equipment', location: null, variant: 'police' },
  'lsa.recovery.police.freeboard':            { type: 'recovery_equipment', location: null, variant: 'police_freeboard' },

  // Pyrotechnics
  'lsa.pyrotechnics.pilot.white':             { type: 'pyrotechnic', location: null, variant: 'pilot_white' },
  'lsa.pyrotechnics.pilot.red':               { type: 'pyrotechnic', location: null, variant: 'pilot_red' },
  'lsa.pyrotechnics.flares':                  { type: 'pyrotechnic', location: null, variant: 'flares' },

  // EPIRB
  'lsa.epirb.cat0':                           { type: 'epirb', location: null, variant: 'cat0' },
  'lsa.epirb.cat1to2':                        { type: 'epirb', location: null, variant: 'cat1to2' },

  // SART
  'lsa.sart':                                 { type: 'sart', location: null },

  // General alarm and PA system
  'lsa.general.alarm':                        { type: 'general_alarm', location: null },
  'lsa.pa.system':                            { type: 'pa_system', location: null },
  
  // Marine evacuation system
  'lsa.evacuation.system':                    { type: 'evacuation_system', location: null },

  // ===== NAVIGATION EQUIPMENT MAPPINGS =====
  // Domain Spine v2: Explicit Navigation equipment mappings
  
  // Navigation Lights and Equipment
  'nav.compass.light':                        { type: 'compass_light', location: null },
  'nav.radar.reflector':                      { type: 'radar_reflector', location: null },
  'nav.radar.reflector.police.cat5':          { type: 'radar_reflector', location: null, variant: 'police_exempt' },
  
  // Electronic Navigation Equipment
  'nav.gps':                                  { type: 'gps', location: null },
  'nav.log.distance':                         { type: 'distance_log', location: null },
  'nav.radar.3cm':                            { type: 'radar', location: null, variant: '3cm' },
  'nav.ais.transceiver':                      { type: 'ais', location: null },
  'nav.ecdis':                                { type: 'ecdis', location: null },
  
  // Navigation Instruments
  'nav.barometer.police':                     { type: 'barometer', location: null, variant: 'police' },
  
  // Navigation Publications
  'nav.publications.police.alternative':      { type: 'publication', location: null, variant: 'electronic' },
  
  // ===== GMDSS EQUIPMENT MAPPINGS =====
  // Domain Spine v2: Complete GMDSS equipment mappings for radio communications
  
  // VHF Radios
  'gmdss.vhf.fixed.dsc':                      { type: 'vhf_radio', location: 'bridge', variant: 'fixed_dsc' },
  'gmdss.vhf.fixed.dsc.optional':             { type: 'vhf_radio', location: 'bridge', variant: 'fixed_dsc' },
  'gmdss.vhf.ch70.watch':                     { type: 'vhf_receiver', location: 'bridge', variant: 'ch70_watch' },
  'gmdss.vhf.portable.dsc':                   { type: 'vhf_radio', location: 'bridge', variant: 'portable_dsc' },
  'gmdss.vhf.portable.multiple':              { type: 'vhf_radio', location: 'liferaft', variant: 'portable' },
  'gmdss.vhf.portable.police':                { type: 'vhf_radio', location: 'bridge', variant: 'police' },
  'gmdss.vhf.portable.protection':            { type: 'equipment_case', location: 'bridge', variant: 'waterproof' },
  'gmdss.vhf.fixed.police.exempt':            { type: 'exemption', location: null, variant: 'police' },
  
  // MF/HF Radios
  'gmdss.mf.dsc':                             { type: 'mf_radio', location: 'radio_room', variant: 'mf_dsc' },
  'gmdss.mfhf.dsc.nbdp':                      { type: 'mfhf_radio', location: 'radio_room', variant: 'mfhf_full' },
  
  // Satellite Communications
  'gmdss.satellite.service':                  { type: 'satellite_terminal', location: 'bridge', variant: 'inmarsat' },
  
  // Distress Alerting Equipment - REMOVED: EPIRBs/PLBs/SARTs are handled by LSA domain only
  // Per WBC3 17.5.3 and 14.12, these items belong to LSA domain
  // NOTE: Any EPIRB/PLB/SART from GMDSS requirements will be forced to LSA category
  // in rowsFromRequirement() to prevent duplication
  
  // Navigation Safety Equipment
  'gmdss.navtex.mandatory':                   { type: 'navtex', location: 'bridge', variant: '518khz' },
  'gmdss.navtex.conditional.a1':              { type: 'navtex', location: 'bridge', variant: '518khz' },
  'gmdss.navtex.conditional.cat5':            { type: 'navtex', location: 'bridge', variant: '518khz' },
  
  // Operator Certification
  'gmdss.roc.operator':                       { type: 'certification', location: null, variant: 'roc' },

  // ===== MEDICAL EQUIPMENT MAPPINGS =====
  // Domain Spine v2: Medical equipment for stores, dangerous goods, and special vessels

  // Medical Stores (category-based)
  'medical.stores.cat.a':                     { type: 'medical_stores', location: null, variant: 'cat_a' },
  'medical.stores.cat.b':                     { type: 'medical_stores', location: null, variant: 'cat_b' },
  'medical.stores.cat.c':                     { type: 'medical_stores', location: null, variant: 'cat_c' },

  // Dangerous Goods Medical
  'medical.dg.kit':                           { type: 'medical_kit', location: null, variant: 'dg' },
  'medical.dg.antidotes':                     { type: 'antidote_kit', location: null, variant: 'dg' },

  // Special Vessel Medical Equipment
  'medical.firstaid.police.pwc':              { type: 'first_aid_kit', location: null, variant: 'police_pwc' },
  'medical.stretcher.pilot':                  { type: 'stretcher', location: null, variant: 'pilot' },
  'medical.kit.rouv':                         { type: 'medical_kit', location: null, variant: 'rouv' },
};

// ——— Guarded choice rows ———
// We'll only add these if a direct requirement for the same item does NOT already exist.
function rowsFromChoices(requirement, vesselId, allReqs) {
  // 🚫 Do not generate equipment rows for type_inflatable (prevents duplication)
  if (requirement.id === 'lsa.lifejackets.type_inflatable') {
    return [];
  }

  const rows = [];
  const chosen = requirement?.evaluation?.fulfilledBy || [];
  const selected = requirement?.evaluation?.selected || {};
  const has = (tokens) => chosen.some(v => tokens.has(toKey(v)));

  // Determine category for choice-based rows (currently all FFE)
  let category = 'FFE'; // default
  
  // CRITICAL: Correct category assignment for all equipment types
  if (requirement.id?.startsWith('lsa.')) {
    category = 'LSA';
  }
  else if (requirement.id?.startsWith('nav_') || requirement.id?.startsWith('nav.')) {
    category = 'Navigation';
  }
  else if (requirement.id?.startsWith('gmdss_')) {
    // GMDSS equipment - but force EPIRB/PLB/SART to LSA
    if (requirement.id?.includes('epirb') || requirement.id?.includes('plb') || requirement.id?.includes('sart')) {
      category = 'LSA'; // Force LSA category for all EPIRB/PLB/SART items
    } else {
      category = 'GMDSS'; // Other GMDSS equipment keeps GMDSS category
    }
  }
  else if (requirement.id?.startsWith('machinery_')) {
    category = 'Machinery';
  }
  else if (requirement.id?.startsWith('electrical_')) {
    category = 'Electrical';
  }
  else if (requirement.id?.startsWith('medical.')) {
    category = 'Medical';
  }

  const TOKENS = {
    OPEN_FLAME: {
      BLANKET: new Set(['fire blanket','blanket']),
      EXTRA_EXT: new Set(['8a/68b','additional 8a/68b extinguisher','additional extinguisher (8a/68b)', 'additional extinguisher', 'extra extinguisher']),
      FIXED: new Set(['fixed suppression system','fixed fire extinguishing system','fixed system']),
    },
    ENGINE_ABOVE: {
      FIXED: new Set(['fixed fire extinguishing system','fixed system']),
    },
  };

  // Open flame protection
  if (requirement.id === 'ffe_protection_open_flame') {
    // Blanket (skip if direct blanket requirement exists)
    if (has(TOKENS.OPEN_FLAME.BLANKET)) {
      const hasDirectBlanket = allReqs.some(r => r.id === 'ffe_fire_blanket_risk_area');
      if (!hasDirectBlanket) {
        rows.push(makeRow({ type: 'blanket', location: 'galley' }, requirement, vesselId, { variant: 'blanket', category }));
      }
    }
    // Fixed galley system (skip if direct galley fixed exists)
    if (has(TOKENS.OPEN_FLAME.FIXED)) {
      const hasDirectGalleyFixed = allReqs.some(r => r.id === 'ffe_fixed_system_galley');
      if (!hasDirectGalleyFixed) {
        rows.push(makeRow({ type: 'fixedSystem', location: 'galley' }, requirement, vesselId, { variant: 'fixed', category }));
      }
    }
    // Extra 8A/68B extinguisher (this is an ADDITIONAL unit)
    if (has(TOKENS.OPEN_FLAME.EXTRA_EXT)) {
      // Always add a distinct "extra" unit so it coexists with any direct unit.
      // Use a different variant so IDs + canonical keys don't collapse the two rows.
      rows.push(
        makeRow(
          { type: 'extinguisher', location: 'galley' },
          requirement,
          vesselId,
          { variant: '8a68b_extra', category }
        )
      );
    }
  }

  // Engine box protection (above deck) - handle both extinguisher and fixed system
  if (requirement.id === 'ffe_extinguisher_enginebox_above') {
    const fixedChosen = selected.fixedSystem || has(TOKENS.ENGINE_ABOVE.FIXED);
    
    if (fixedChosen) {
      // User chose fixed system - only add fixed system (no base extinguisher)
      const hasDirectEngineFixed = allReqs.some(r => r.id === 'ffe_fixed_system_engine');
      if (!hasDirectEngineFixed) {
        rows.push(makeRow({ type: 'fixedSystem', location: 'engine' }, requirement, vesselId, { variant: 'fixed', category }));
      }
    } else {
      // User chose portable extinguisher - add base extinguisher only
      rows.push(makeRow({ type: 'extinguisher', location: 'engine' }, requirement, vesselId, { variant: 'base', category }));
    }
  }

  return rows;
}

// ——— Direct mapping → row ———
function makeRow(cfg, requirement, vesselId, { variant = 'base', category = 'FFE' } = {}) {
  const type = cfg.type;
  const location = cfg.location || inferLocationFromName(requirement?.name || requirement?.item);
  const label = labelFor({ type, location, variant, lifejacketType: requirement.lifejacketType });

  // Ensure proper guidance key propagation
  const sourceRequirementId = requirement.id;
  const guidanceKey = sourceRequirementId || requirement.id;

  // Add all guidance flags that UI and audit expect to find on each row
  const ffeGuidance = getFFEGuidance(sourceRequirementId);
  const lsaGuidance = getLSAGuidance(sourceRequirementId);
  const navGuidance = getNAVGuidance(sourceRequirementId);
  const gmdssGuidance = getGMDSSGuidance(sourceRequirementId);
  const electricalGuidance = getElectricalGuidance(sourceRequirementId);
  const machineryGuidance = getMachineryGuidance(sourceRequirementId);
  const generalGuidance = getGeneralGuidance(sourceRequirementId);

  // Preserve selected requirement fields that are important for maintenance logic
  const preservedFields = {};
  if (requirement.lifejacketType) preservedFields.lifejacketType = requirement.lifejacketType;
  if (requirement.qtyRequired) preservedFields.qtyRequired = requirement.qtyRequired;

  return {
    id: rowId({ vesselId, type, location, variant, sourceRequirementId }),
    category,
    type,
    location,
    variant, // Add variant to the returned object
    spec: cfg.spec || {},
    sourceRequirementId, // FIXED: singular, always set to canonical requirement ID
    sourceRequirementIds: [requirement.id], // Keep for backwards compatibility
    covers: cfg.covers || [],
    name: label,
    label,
    description: requirement.description,
    reference: requirement.reference,
    regulation: requirement.reference, // Backward compatibility
    mandatory: requirement.mandatory,
    guidanceKey, // FIXED: always set to canonical requirement ID
    
    // CRITICAL: Add all guidance fields that UI and audit expect
    ffeGuidance,
    lsaGuidance,
    navGuidance,
    gmdssGuidance,
    electricalGuidance,
    machineryGuidance,
    generalGuidance,
    environmentalGuidance: null, // Placeholder for future Environmental guidance
    operationsGuidance: null,   // Placeholder for future Operations guidance
    
    // Preserve important requirement fields for maintenance logic
    ...preservedFields,
    
    sourceVersion: '1.0.0',
    source: 'requirements',
    generatedAt: new Date().toISOString(),
    // user fields placeholders (the hook preserves on merge)
    serialNo: null,
    installedAt: null,
    lastServiceAt: null,
    notes: null,
    units: [],  // CRITICAL: Add units field so it exists from day 1
    photos: [],
    customFields: {},
  };
}

// ——— Requirement → rows ———
function rowsFromRequirement(req, vesselId, allReqs) {
  // 🚫 Do not generate equipment rows for generic BASIC lifejackets
  if (req.id === 'basic_lifejackets') {
    return [];
  }

  // 🚫 Do not generate equipment rows for type_inflatable (prevents duplication)
  if (req.id === 'lsa.lifejackets.type_inflatable') {
    return [];
  }

  if (DIRECT[req.id]) {
    // Determine category based on requirement ID prefix
    let category = 'FFE'; // default
    
    // CRITICAL: Correct category assignment for all equipment types
    if (req.id.startsWith('lsa.')) {
      category = 'LSA';
    } 
    else if (req.id.startsWith('nav_') || req.id.startsWith('nav.')) {
      category = 'Navigation';
    }
    else if (req.id.startsWith('gmdss_') || req.id.startsWith('gmdss.')) {
      // GMDSS equipment - but force EPIRB/PLB/SART to LSA
      if (req.id.includes('epirb') || req.id.includes('plb') || req.id.includes('sart')) {
        category = 'LSA'; // Force LSA category for all EPIRB/PLB/SART items
      } else {
        category = 'GMDSS'; // Other GMDSS equipment keeps GMDSS category
      }
    }
    else if (req.id.startsWith('machinery_')) {
      category = 'Machinery';
    }
    else if (req.id.startsWith('electrical_')) {
      category = 'Electrical';
    }
    else if (req.id.startsWith('medical.')) {
      category = 'Medical';
    }

    const row = makeRow(DIRECT[req.id], req, vesselId, { variant: DIRECT[req.id].variant, category });
    
    // Debug logging for lifejacket rows
    if (req.id.includes('lifejacket')) {

    }
    
    return [row];
  }
  return rowsFromChoices(req, vesselId, allReqs);
}

// ——— Public API ———
export function generateEquipmentRegisterRows(vesselConfig, vesselId, category = 'FFE') {
  if (!vesselConfig || !vesselId) return [];
  const reqs = getCanonicalRequirementsByCategory(vesselConfig, category) || [];
  
  // Debug logging for equipment generation

  if (category === 'FFE') {
    // FFE-specific debug logging for gas detector requirement
    const gasDetectorReq = reqs.find(r => r.id === 'ffe_detector_gas_galley');
    if (gasDetectorReq) {

    } else {


    }
  }
  
  const rows = [];
  reqs.forEach((r) => rows.push(...rowsFromRequirement(r, vesselId, reqs)));

  // Final in-mapper dedupe by canonical key (belt & braces)
  const byKey = new Map();
  rows.forEach((row) => {
    const k = canonicalKey(row);
    if (!byKey.has(k)) byKey.set(k, row);
  });
  
  let result = Array.from(byKey.values());
  
  // Enhanced de-duplication for lifejackets and TPAs
  if (category === 'LSA') {
    const deduped = [];
    const seenTypes = new Map();
    
    for (const row of result) {
      const dedupeKey = `${row.type}_${row.variant || 'base'}_${row.location || 'null'}`;
      
      // For lifejackets: prefer specific domain requirements over basic ones
      if (row.type === 'lifejacket') {
        // Extra safety: skip any basic_lifejackets that somehow got through
        if (row.sourceRequirementId === 'basic_lifejackets') {
          continue; // skip basic lifejackets completely
        }
        
        const existing = seenTypes.get(dedupeKey);
        if (existing) {
          // Keep the more specific one (domain-specific over basic)
          const currentIsBasic = (row.sourceRequirementId === 'basic_lifejackets');
          const existingIsBasic = (existing.sourceRequirementId === 'basic_lifejackets');
          
          if (!currentIsBasic && existingIsBasic) {
            // Replace basic with domain-specific
            const existingIndex = deduped.indexOf(existing);
            deduped[existingIndex] = row;
            seenTypes.set(dedupeKey, row);
          } else if (currentIsBasic && !existingIsBasic) {
            // Skip this basic one, keep domain-specific
            continue;
          }
        } else {
          deduped.push(row);
          seenTypes.set(dedupeKey, row);
        }
      }
      // For TPAs: only keep one per type/variant combination  
      else if (row.type === 'tpa') {
        const existing = seenTypes.get(dedupeKey);
        if (existing) {
          // Keep the more specific one (wizard-selected over category-based)
          const currentIsWizard = (row.sourceRequirementId === 'lsa.tpa.wizard');
          const existingIsWizard = (existing.sourceRequirementId === 'lsa.tpa.wizard');
          
          if (currentIsWizard && !existingIsWizard) {
            // Replace category-based with wizard-selected
            const existingIndex = deduped.indexOf(existing);
            deduped[existingIndex] = row;
            seenTypes.set(dedupeKey, row);
          } else if (!currentIsWizard && existingIsWizard) {
            // Skip this category-based one, keep wizard-selected
            continue;
          }
        } else {
          deduped.push(row);
          seenTypes.set(dedupeKey, row);
        }
      }
      // For all other types: just add normally
      else {
        deduped.push(row);
      }
    }
    
    result = deduped;
  }
  
  // Log equipment being generated
  const categoryItems = result.filter(eq => eq.category === category);


  if (category === 'FFE') {
    // Specifically check for gas detector
    const gasDetector = categoryItems.find(eq => eq.sourceRequirementId === 'ffe_detector_gas_galley');
    if (gasDetector) {

    } else {

    }
  }
  
  return result;
}

export const __debugCanonicalKey = (row) => canonicalKey(row);

// Browser debug helper to dump full LSA equipment model
if (typeof window !== 'undefined') {
  window.__debugDumpLSAEquipmentModel = function () {

    const all = [];

    // 1) From the DIRECT mapping object (look for LSA-related entries)
    Object.entries(DIRECT).forEach(([key, value]) => {
      if (key.toLowerCase().includes('lsa') || 
          key.toLowerCase().includes('life') ||
          key.toLowerCase().includes('basic_life')) {
        all.push({
          sourceRequirementId: key,
          ...value,
        });
      }
    });

    // 2) Get LSA requirements from canonical requirements system
    // Use a basic vessel config to get maximum possible LSA equipment
    const maxVesselConfig = {
      basic: { category: 2, lengthOverall: 24, operatingStatus: 'seagoing' },
      capacity: { passengerCapacity: 12, crewSize: 3, pob: 15 },
      accommodation: { hasAccommodation: true },
      environmental: { hasSubstantialEnclosure: true },
      operations: { operations: ['commercial_fishing', 'passenger_transport'] }
    };

    try {
      const lsaRequirements = getCanonicalRequirementsByCategory(maxVesselConfig, 'LSA') || [];

      lsaRequirements.forEach(requirement => {
        // Check if we already have this from DIRECT mapping
        const existingIndex = all.findIndex(item => item.sourceRequirementId === requirement.id);
        
        if (existingIndex === -1) {
          // Create equipment model from requirement
          const equipmentModel = {
            sourceRequirementId: requirement.id,
            name: requirement.name || requirement.label || requirement.id,
            category: requirement.category || 'LSA',
            type: requirement.type || 'equipment',
            location: requirement.location || inferLocationFromName(requirement.name || requirement.id),
            description: requirement.description,
            reference: requirement.reference,
            mandatory: requirement.mandatory,
            // Add any additional requirement fields
            ...Object.keys(requirement).reduce((acc, key) => {
              if (!['id', 'name', 'label', 'category', 'type', 'location', 'description', 'reference', 'mandatory'].includes(key)) {
                acc[key] = requirement[key];
              }
              return acc;
            }, {})
          };
          
          all.push(equipmentModel);
        }
      });
    } catch (error) {

    }

    // Remove duplicates by sourceRequirementId
    const unique = Array.from(
      new Map(all.map(i => [i.sourceRequirementId, i])).values()
    );


    return unique;
  };

  // LSA register debug helper
  window.__debugDumpLSARegister = function () {

    try {
      // First, try to get LSA items from the actual canonical register if available
      const canonicalKey = localStorage.getItem('current_vessel_id') || 'VSL001-25';
      const registerKey = `equipment_register_${canonicalKey}`;
      const registerData = localStorage.getItem(registerKey);
      
      if (registerData) {
        const allEquipment = JSON.parse(registerData);
        const lsaItems = allEquipment.filter(eq => eq.category === 'LSA');


        if (lsaItems.length > 0) {


          return lsaItems;
        }
      }
      
      // Fallback: Generate LSA equipment using mapper directly

      const basicVesselConfig = {
        basic: { category: 2, lengthOverall: 12, operatingStatus: 'seagoing' },
        capacity: { passengerCapacity: 8, crewSize: 2, pob: 10 },
        accommodation: { hasAccommodation: true },
        environmental: { hasSubstantialEnclosure: true }
      };
      
      const vesselId = 'DEBUG_VESSEL';
      const lsaRows = generateEquipmentRegisterRows(basicVesselConfig, vesselId, 'LSA');


      return lsaRows;
    } catch (error) {

      return [];
    }
  };
}